
import '@babylonjs/loaders';
// // Side-effects only imports allowing the standard material to be used as default.
import '@babylonjs/core/Materials/standardMaterial';
// // Side-effects only imports allowing Mesh to create default shapes (to enhance tree shaking, the construction methods on mesh are not available if the meshbuilder has not been imported).
import '@babylonjs/core/Meshes/Builders/sphereBuilder';
import '@babylonjs/core/Meshes/Builders/boxBuilder';
import '@babylonjs/core/Meshes/Builders/groundBuilder';

import { Vector3, Vector4, MeshBuilder, Mesh, Scene, UniversalCamera, HemisphericLight, Color3, SpotLight, Camera, StandardMaterial, Texture, Light, ShadowGenerator, IShadowLight, PBRMaterial, MirrorTexture, Plane } from "@babylonjs/core";
import { GridMaterial } from "@babylonjs/materials";
import * as _ from 'lodash';
import { getSizeForWorld } from '../../../utils/math';


export const createUniversalCamera = (scene: Scene): UniversalCamera => {
  const originAngle = Math.random() * Math.PI;
  const originRadius = 10;
  const xzPosition = {
    x: Math.sin(originAngle) * originRadius,
    z: Math.cos(originAngle) * originRadius,
  };

  const camera = new UniversalCamera('Camera', new Vector3(xzPosition.x, 5, xzPosition.z), scene);
  camera.fov = 1;
  camera.position = new Vector3(5, 1, 5);
  camera.target = new Vector3(-1, -2, -1);
  camera.keysUp = [];
  camera.keysDown = [];
  camera.keysLeft = []
  camera.keysRight = []
  // camera.keysUp = [87, 38]; // w, up arrow
  // camera.keysDown = [83, 40]; // s, down arrow
  // camera.keysLeft = [65 ,37] // a, left arrow
  // camera.keysRight = [68 ,39] // d, right arrow
  // camera.angularSensibility = 5500; // default 2000 (higher reduces)
  // camera.touchAngularSensibility = 200000; // default 200000
  // camera.touchMoveSensibility = 250; // default 250
  // camera.inertia = 0.8;
  // camera.speed = 0.9;
  // console.log(`cam sens nums: inertia ${camera.inertia}, speed ${camera.speed}, touchMove ${camera.angularSensibility}`);
  
  return camera;
}

export const createLight = (scene: Scene, camera: Camera) => {
  const sunLight = new HemisphericLight('hemiLight', new Vector3(0, 1, 0), scene);
  sunLight.diffuse = new Color3(1, 1, 1);
  sunLight.specular = new Color3(0, 0, 0);
  sunLight.groundColor = new Color3(1, 1, 1);
  sunLight.intensity = 0.9;
  (window as any)['sunLight'] = sunLight;

  const moonLight = new SpotLight('moonLight', new Vector3(0, 100, 0), new Vector3(0, -1, 0), Math.PI / 2, 10, scene);
  moonLight.diffuse = new Color3(1, 1, 1);
  moonLight.specular = new Color3(0, 0, 0);
  moonLight.intensity = 0.25;
  moonLight.parent = camera;
  (window as any)['moonlight'] = moonLight;

  return { sunLight, moonLight };
}

export const createShadowGenerator = (scene: Scene, light: IShadowLight) => {
  const shadowGenerator = new ShadowGenerator(1024, light);
  shadowGenerator.useBlurExponentialShadowMap = true;
  shadowGenerator.blurKernel = 32;
  return shadowGenerator;
}

export const createSky = (scene: Scene) => {
  const skybox = Mesh.CreateBox('skyBox', 1000.0, scene);
  const skyboxMaterial = new StandardMaterial('skyMaterial', scene);
  skyboxMaterial.backFaceCulling = false;
  skyboxMaterial.diffuseColor = new Color3(40/255, 55/255, 75/255);
  skyboxMaterial.alpha = 0.999;
  // skyboxMaterial.diffuseColor = new Color3(51/255, 52/255, 74/255);
  skybox.material = skyboxMaterial;

  return skybox;
}

export const createGroundReflectiveTexture = (scene: Scene) => {
  const mirrorTexture = new MirrorTexture('mirror', 512, scene, true);
  mirrorTexture.mirrorPlane = new Plane(0, -1, 0, -0.1);
  mirrorTexture.renderList = [];
  mirrorTexture.level = 0.6; // Adjust the level of reflection
  mirrorTexture.blurKernel = 24;

  return mirrorTexture;
}

export const createGround = (scene: Scene, mirrorTexture: MirrorTexture) => {
  const extend = scene.getWorldExtends();
  const { width, depth } = getSizeForWorld(extend);

  const ground = Mesh.CreateGround('grid', 1.0, 1.0, 1, scene);
  ground.scaling.z = ground.scaling.x = Math.max(width, depth);
  ground.isPickable = false;
  // ground.receiveShadows = true;

  const groundMaterial = new PBRMaterial('groundMaterial', scene);

  // Assign the mirror texture to the ground material
  groundMaterial.reflectionTexture = mirrorTexture;
  groundMaterial.microSurface = 0.5;
  groundMaterial.reflectivityColor = new Color3(0.1, 0.1, 0.1);
  groundMaterial.albedoColor = new Color3(0, 0, 0);

  // Assign the material to the ground
  ground.material = groundMaterial;

  return ground;
  
  // const gridMaterial = new GridMaterial('gridMaterial', scene);
  // gridMaterial.majorUnitFrequency = 10;
  // gridMaterial.minorUnitVisibility = 0.3;
  // gridMaterial.gridRatio = 0.01;
  // gridMaterial.backFaceCulling = false;
  // gridMaterial.mainColor = new Color3(1, 1, 1);
  // gridMaterial.lineColor = new Color3(1.0, 1.0, 1.0);
  // gridMaterial.opacity = 0.8;
  // gridMaterial.zOffset = 1.0;
  // gridMaterial.opacityTexture = new Texture('/assets/3d/background-ground.png', scene);

  // ground.material = gridMaterial;
}

export const createGroundColor = (scene: Scene) => {
  // const ground = Mesh.CreateGround('ground', 1, 1, 1, scene);

  // const extend = scene.getWorldExtends();
  // const { width, depth } = getSizeForWorld(extend);
  // ground.scaling.z = ground.scaling.x = Math.max(width, depth);
  // ground.isPickable = false;
  // // ground.receiveShadows = true;
  // ground.visibility = 0.5;

  // const material = new StandardMaterial('groundMaterial', scene);
  // material.diffuseColor = new Color3(51/255, 52/255, 74/255);
  // ground.material = material;
}

export const createWelcomeBox = (scene: Scene) => {
  const box = MeshBuilder.CreateBox('welcome-box', { size: 0.5 }, scene);
  box.rotation = new Vector3(Math.PI / 4, Math.PI / 4, Math.PI / 4);
  return box;
}

export const createStage = (scene: Scene) => {
  const box = MeshBuilder.CreateBox('stage-box', {
    width: 20,
    height: 3,
    depth: 10,
  }, scene);
  box.position.z = -40;
  return box;
}

export const rgbToColor = (rgb: number[]) => {
  const color = _.map(rgb, val => val / 255);
  return color;
}
